import React, { useRef, useState, useCallback, useEffect } from "react"
import { Slider, Box, styled } from "@mui/material"

import { colors } from "@/components/colors"
import { TimeUtils } from "./timeUtils"
import { TimeFormat } from "./timeTypes"

interface TimeRangeSliderProps {
  value: number[]
  onChange: (event: Event, value: number | number[], activeThumb: number) => void
  onChangeCommitted: (event: Event, newValue: number | number[]) => void
  min?: number
  max?: number
  step?: number
  minDistance?: number
  boundaryMin?: number
  boundaryMax?: number
  format?: TimeFormat
}

interface DragState {
  isDragging: boolean
  startX: number
  initialValues: number[]
  currentValues: number[]
}

const StyledSlider = styled(Slider)(({ theme }) => ({
  color: colors.platinum["900"],
  height: 8,
  padding: "8px 0",
  margin: "0px",
  "& .MuiSlider-thumb": {
    height: 18,
    width: 18,
    backgroundColor: colors.neutral.white,
    border: "2px solid" + colors.platinum["900"],
    transition: "none",
    "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": {
      boxShadow: "0 0 0 8px rgba(85, 85, 85, 0.16)",
    },
    "&:before": {
      display: "none",
    },
    "&::after": {
      width: 20,
      height: 20,
    },
    "&:last-of-type": {
      zIndex: 3,
    },
    "&:first-of-type": {
      zIndex: 2,
    },
  },
  "& .MuiSlider-valueLabel": {
    backgroundColor: colors.neutral.white,
    color: colors.platinum["900"],
    border: "1px solid " + colors.platinum["400"],
    borderRadius: "4px",
    padding: "4px 8px",
    fontSize: "12px",
    top: "-10px",
    zIndex: 100,
    "&:before": {
      display: "none",
    },
  },
  "& .MuiSlider-track": {
    border: "none",
    height: 8,
    backgroundColor: colors.platinum["900"],
    transition: "none",
  },
  "& .MuiSlider-rail": {
    opacity: 1,
    backgroundColor: colors.platinum["400"],
    height: 8,
  },
  "& .MuiSlider-mark": {
    backgroundColor: colors.neutral.alpha["black-12"],
    height: 4,
    width: 1,
    marginTop: 8,
    "&.MuiSlider-markActive": {
      opacity: 1,
      backgroundColor: colors.neutral.alpha["black-12"],
    },
  },
  "& .MuiSlider-markLabel": {
    fontSize: "12px",
    color: colors.neutral.grey["600"],
    top: "30px",
  },
}))

export default function TimeRangeSlider({
  value,
  onChange,
  onChangeCommitted,
  min = 0,
  max = 100,
  step = 1,
  minDistance = 1,
  boundaryMin,
  boundaryMax,
  format = "HH:MM",
}: TimeRangeSliderProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const dragStateRef = useRef<DragState>({
    isDragging: false,
    startX: 0,
    initialValues: [0, 0],
    currentValues: [0, 0],
  })
  const [isDraggingTrack, setIsDraggingTrack] = useState(false)

  const propsRef = useRef({ onChange, onChangeCommitted, min, max, step, boundaryMin, boundaryMax })
  propsRef.current = { onChange, onChangeCommitted, min, max, step, boundaryMin, boundaryMax }

  const getTrackOverlayStyle = useCallback(() => {
    const range = max - min
    const startPercent = ((value[0] - min) / range) * 100
    const endPercent = ((value[1] - min) / range) * 100
    const thumbOffset = 10

    return {
      left: `calc(16px + ${startPercent}% + ${thumbOffset}px)`,
      width: `calc(${endPercent - startPercent}% - ${thumbOffset * 2}px)`,
    }
  }, [value, min, max])

  const handleOverlayMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      dragStateRef.current = {
        isDragging: true,
        startX: e.clientX,
        initialValues: [...value],
        currentValues: [...value],
      }
      setIsDraggingTrack(true)
    },
    [value],
  )

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragStateRef.current.isDragging || !containerRef.current) {
        return
      }

      const { min, max, step, boundaryMin, boundaryMax, onChange } = propsRef.current
      const effectiveMin = boundaryMin ?? min
      const effectiveMax = boundaryMax ?? max

      const sliderWidth = containerRef.current.offsetWidth - 32
      const valueRange = max - min
      const deltaX = e.clientX - dragStateRef.current.startX
      const deltaValue = (deltaX / sliderWidth) * valueRange

      const [initialStart, initialEnd] = dragStateRef.current.initialValues
      const rangeWidth = initialEnd - initialStart

      let newStart = initialStart + deltaValue
      let newEnd = initialEnd + deltaValue

      if (newStart < effectiveMin) {
        newStart = effectiveMin
        newEnd = effectiveMin + rangeWidth
      }
      if (newEnd > effectiveMax) {
        newEnd = effectiveMax
        newStart = effectiveMax - rangeWidth
      }

      newStart = Math.round(newStart / step) * step
      newEnd = Math.round(newEnd / step) * step

      const [prevStart, prevEnd] = dragStateRef.current.currentValues
      if (newStart !== prevStart || newEnd !== prevEnd) {
        dragStateRef.current.currentValues = [newStart, newEnd]
        const syntheticEvent = new Event("change") as Event
        onChange(syntheticEvent, [newStart, newEnd], -1)
      }
    }

    const handleMouseUp = () => {
      if (!dragStateRef.current.isDragging) return

      const currentValues = dragStateRef.current.currentValues
      dragStateRef.current.isDragging = false
      setIsDraggingTrack(false)

      const syntheticEvent = new Event("change") as Event
      propsRef.current.onChangeCommitted(syntheticEvent, currentValues)
    }

    if (isDraggingTrack) {
      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
      return () => {
        document.removeEventListener("mousemove", handleMouseMove)
        document.removeEventListener("mouseup", handleMouseUp)
      }
    }
  }, [isDraggingTrack])

  const restrictedAreaStyle = {
    position: "absolute",
    height: "8px",
    backgroundColor: "rgba(0,0,0,0.1)",
    pointerEvents: "none",
    top: "50%",
    transform: "translateY(-50%)",
  }

  const marks = Array.from({ length: 7 }, (_, i) => {
    const value = (max / 6) * i

    let label = ""
    if (format === "HH:MM:SS.ms") {
      const totalSeconds = value / 10
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = Math.floor(totalSeconds % 60)
      label = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
    } else if (format === "HH:MM:SS") {
      const hours = Math.floor(value / 3600)
      const minutes = Math.floor((value % 3600) / 60)
      const seconds = value % 60
      label = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
    } else {
      const hours = Math.floor(value / 60)
      const minutes = Math.floor(value % 60)
      label = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`
    }

    return {
      value: value,
      label: label,
    }
  })

  const trackOverlayStyle = getTrackOverlayStyle()

  return (
    <Box ref={containerRef} sx={{ flex: 1, px: 2, position: "relative" }}>
      {boundaryMin > min && (
        <Box
          sx={{
            ...restrictedAreaStyle,
            left: "16px",
            width: `${((boundaryMin - min) / (max - min)) * 100}%`,
          }}
        />
      )}
      {boundaryMax < max && (
        <Box
          sx={{
            ...restrictedAreaStyle,
            right: "16px",
            width: `${((max - boundaryMax) / (max - min)) * 100}%`,
          }}
        />
      )}

      <StyledSlider
        value={value}
        onChange={onChange}
        onChangeCommitted={onChangeCommitted}
        min={min}
        max={max}
        step={step}
        minDistance={minDistance}
        marks={marks}
        disableSwap
        valueLabelDisplay="auto"
        valueLabelFormat={(value) => TimeUtils.format(value, format)}
      />

      <Box
        onMouseDown={handleOverlayMouseDown}
        sx={{
          position: "absolute",
          height: "12px",
          top: "50%",
          transform: "translateY(-50%)",
          cursor: isDraggingTrack ? "grabbing" : "grab",
          zIndex: 1,
          ...trackOverlayStyle,
        }}
      />
    </Box>
  )
}
